import { FC } from 'react';

import StrongUpIcon from '/public/images/iqfu/public/market/strong_up_icon.svg';
import UpIcon from '/public/images/iqfu/public/market/up_icon.svg';
import FlatIcon from '/public/images/iqfu/public/market/flat_icon.svg';
import DownIcon from '/public/images/iqfu/public/market/down_icon.svg';
import StrongDownIcon from '/public/images/iqfu/public/market/strong_down_icon.svg';

import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';

import { SalesStatsData } from '@type-def_iqfu/public/MarketData';

import { PublicAreaTypes, PublicPropertyType, PublicTrendType } from '@constants_iqfu';

import styles from './PricePageMarketInfo.module.scss';
import AverageSaleCaseCountLastYear from './trend_average_top_items/AverageSaleCaseCountLastYear';
import AverageSalePeriodByDayLastYear from './trend_average_top_items/AverageSalePeriodByDayLastYear';
import AverageSalePriceLastYear from './trend_average_top_items/AverageSalePriceLastYear';
import AverageBuildingAge from './trend_card/average_building_age/AverageBuildingAge';
import AverageBuildingArea from './trend_card/average_building_area/AverageBuildingArea';
import AverageFloorPlan from './trend_card/average_floor_plan/AverageFloorPlan';
import AverageLandArea from './trend_card/average_land_area/AverageLandArea';
import AverageOccupiedArea from './trend_card/average_occupied_area/AverageOccupiedArea';
import AverageSalePricePerSquare from './trend_card/average_sale_price_per_square/AverageSalePricePerSquare';

type Props = {
  displayData: SalesStatsData;
  propertyType: keyof typeof PublicPropertyType;
  pageType: keyof typeof PublicAreaTypes;
  areaInfo: { prefecture?: string; city?: string; parent_major_city?: string; city_category?: string };
};

const PricePageMarketInfo: FC<Props> = ({ propertyType, pageType, displayData, areaInfo }) => {
  const displayAreaName = (areaType?: string) => {
    switch (areaType) {
      case 'country':
        return '全国';
      case 'prefecture':
        return areaInfo.prefecture ?? '';
      case 'city':
        return areaInfo.city ?? '';
      case 'major_city':
        return areaInfo.city_category === 'major' ? areaInfo.city ?? '' : areaInfo.parent_major_city ?? '';
      default:
        return '';
    }
  };

  const displayUniqueQuoteYear = () => {
    //引用に使用するyearのデータを適宜追加する
    const yearsList = [
      displayData.average_sale_period_by_day_last_year?.year,
      displayData.average_sale_price_last_year?.year,
      displayData.sale_case_count_last_year?.year,
      displayData.average_sale_price_per_square.current?.year,
      displayData.average_sale_price_per_square.previous?.year,
      displayData.average_building_area.current?.year,
      displayData.average_building_area.previous?.year,
      displayData.average_occupied_area.current?.year,
      displayData.average_occupied_area.previous?.year,
      displayData.average_building_age.current?.year,
      displayData.average_building_age.previous?.year,
      displayData.average_floor_plan.current?.year,
      displayData.average_floor_plan.previous?.year,
      displayData.average_building_area.current?.year,
      displayData.average_building_area.previous?.year,
    ];
    return Array.from(new Set(yearsList))
      .sort()
      .map((year) => {
        return year && year + '年';
      });
  };

  return (
    <>
      {displayData.sales_markets && (
        <div className={styles.market_info}>
          <div className={styles.market_info_title}>
            <SectionLeadLeftAlign>
              【最新】{displayAreaName(pageType)}の{PublicPropertyType[propertyType]}
              売却の「動向」
            </SectionLeadLeftAlign>
          </div>
          <h3 className={styles.market_info_lead}>{PublicPropertyType[propertyType]}売却相場の動き</h3>

          <ul className={styles.trend_icon_list}>
            {displayData.sales_markets.urban_area.summary_code?.value && (
              <div>
                <li className={styles.trend_icon_item}>
                  <div className={styles.area_text}>都市部</div>
                  <div className={styles.trend_property_arrow}>
                    <TrendIcon type={String(displayData.sales_markets.urban_area.summary_code.value)} />
                  </div>
                  <span className={styles.trend_text}>
                    {
                      PublicTrendType[
                        displayData.sales_markets.urban_area.summary_code.value as keyof typeof PublicTrendType
                      ]
                    }
                  </span>
                </li>
              </div>
            )}

            {displayData.sales_markets.suburban_area.summary_code?.value && (
              <div>
                <li className={styles.trend_icon_item}>
                  <div className={styles.area_text}>郊外部</div>
                  <div className={styles.trend_property_arrow}>
                    <TrendIcon type={String(displayData.sales_markets.suburban_area.summary_code.value)} />
                  </div>
                  <span className={styles.trend_text}>
                    {
                      PublicTrendType[
                        displayData.sales_markets.suburban_area.summary_code.value as keyof typeof PublicTrendType
                      ]
                    }
                  </span>
                </li>
              </div>
            )}

            {displayData.sales_markets.local_area.summary_code?.value && (
              <div>
                <li className={styles.trend_icon_item}>
                  <div className={styles.area_text}>地方</div>
                  <div className={styles.trend_property_arrow}>
                    <TrendIcon type={String(displayData.sales_markets.local_area.summary_code.value)} />
                  </div>
                  <span className={styles.trend_text}>
                    {
                      PublicTrendType[
                        displayData.sales_markets.local_area.summary_code.value as keyof typeof PublicTrendType
                      ]
                    }
                  </span>
                </li>
              </div>
            )}
          </ul>
          <span className={styles.trend_reference_annotation}>※平米単価相場の参考値</span>
        </div>
      )}

      <h3 className={styles.market_trend_lead}>
        {/* 仕様：リード文の年とエリアは一番上のコンポーネント（売却成約件数）と一致させる */}
        {displayData.average_sale_price_this_year?.year}年売れた{PublicPropertyType[propertyType]}の
        <br className="sp_only" />
        傾向と推移（{displayAreaName(displayData?.average_sale_price_this_year?.area_type)}）
      </h3>

      <div className={styles.market_trend_container}>
        <div className={styles.market_trend_items}>
          <AverageSaleCaseCountLastYear
            displayData={displayData}
            areaName={displayAreaName(displayData.sale_case_count_last_year?.area_type)}
            propertyType={propertyType}
          />
          <AverageSalePriceLastYear
            displayData={displayData}
            areaName={displayAreaName(displayData.average_sale_price_last_year?.area_type)}
            propertyType={propertyType}
          />
          <AverageSalePeriodByDayLastYear
            displayData={displayData}
            areaName={displayAreaName(displayData.average_sale_period_by_day_last_year?.area_type)}
            propertyType={propertyType}
          />
          <AverageSalePricePerSquare
            displayData={displayData}
            areaName={displayAreaName(displayData.average_sale_price_per_square.current.area_type)}
            propertyType={propertyType}
          />
          {propertyType === 'house' && (
            <AverageBuildingArea
              displayData={displayData}
              areaName={displayAreaName(displayData.average_building_area.current.area_type)}
              propertyType={propertyType}
            />
          )}
          {(propertyType === 'mansion' || propertyType === 'land') && (
            <AverageOccupiedArea
              displayData={displayData}
              areaName={displayAreaName(displayData.average_occupied_area.current.area_type)}
              propertyType={propertyType}
            />
          )}
          {(propertyType === 'mansion' || propertyType === 'house') && (
            <AverageBuildingAge
              displayData={displayData}
              areaName={displayAreaName(displayData.average_building_age.current.area_type)}
              propertyType={propertyType}
            />
          )}
          {(propertyType === 'mansion' || propertyType === 'house') && (
            <AverageFloorPlan
              displayData={displayData}
              areaName={displayAreaName(displayData.average_floor_plan.current.area_type)}
              propertyType={propertyType}
            />
          )}
          {propertyType === 'house' && (
            <AverageLandArea
              displayData={displayData}
              areaName={displayAreaName(displayData.average_building_area.current.area_type)}
              propertyType={propertyType}
            />
          )}
        </div>

        <p className={styles.trend_quote_annotation}>
          データ：イクラ不動産{displayUniqueQuoteYear()}
          {PublicPropertyType[propertyType]}売却 実績データより
        </p>
      </div>
    </>
  );
};

type TrendIconProps = {
  type: string;
};

const TrendIcon: FC<TrendIconProps> = ({ type }) => {
  return (
    <>
      {type === 'flat' && <FlatIcon />}
      {type === 'strong_up' && <StrongUpIcon />}
      {type === 'up' && <UpIcon />}
      {type === 'down' && <DownIcon />}
      {type === 'strong_down' && <StrongDownIcon />}
    </>
  );
};

export default PricePageMarketInfo;
